import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAdminUserSettings,
  updateAdminUserSettings,
  getPaymentSettings,
  updatePaymentSettings,
  getShippingSettings,
  getStoreSettings,
  AdminUserSettings,
  UpdateAdminUserSettingsRequest,
  PaymentSettings,
  UpdatePaymentSettingsRequest,
  ShippingSettings,
  StoreSettings,
} from "@/services/settingsService";

export interface SettingsState {
  adminUser: {
    data: AdminUserSettings | null;
    loading: boolean;
    error: string | null;
  };
  payment: {
    data: PaymentSettings | null;
    loading: boolean;
    error: string | null;
  };
  shipping: {
    data: ShippingSettings | null;
    loading: boolean;
    error: string | null;
  };
  store: {
    data: StoreSettings | null;
    loading: boolean;
    error: string | null;
  };
}

const initialState: SettingsState = {
  adminUser: {
    data: null,
    loading: false,
    error: null,
  },
  payment: {
    data: null,
    loading: false,
    error: null,
  },
  shipping: {
    data: null,
    loading: false,
    error: null,
  },
  store: {
    data: null,
    loading: false,
    error: null,
  },
};

// Admin User Settings Thunks
export const fetchAdminUserSettings = createAsyncThunk(
  "settings/fetchAdminUserSettings",
  async (storeId: string, { rejectWithValue }) => {
    try {
      const response = await getAdminUserSettings(storeId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const updateAdminUserSettingsAsync = createAsyncThunk(
  "settings/updateAdminUserSettings",
  async (
    {
      storeId,
      settings,
    }: { storeId: string; settings: UpdateAdminUserSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateAdminUserSettings(storeId, settings);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// Payment Settings Thunks
export const fetchPaymentSettings = createAsyncThunk(
  "settings/fetchPaymentSettings",
  async (storeId: string, { rejectWithValue }) => {
    try {
      const response = await getPaymentSettings(storeId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const updatePaymentSettingsAsync = createAsyncThunk(
  "settings/updatePaymentSettings",
  async (
    {
      storeId,
      settings,
    }: { storeId: string; settings: UpdatePaymentSettingsRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updatePaymentSettings(storeId, settings);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// Shipping Settings Thunks
export const fetchShippingSettings = createAsyncThunk(
  "settings/fetchShippingSettings",
  async (storeId: string, { rejectWithValue }) => {
    try {
      const response = await getShippingSettings(storeId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// Store Settings Thunks
export const fetchStoreSettings = createAsyncThunk(
  "settings/fetchStoreSettings",
  async (storeId: string, { rejectWithValue }) => {
    try {
      const response = await getStoreSettings(storeId);
      return response;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    clearSettingsError: (state) => {
      state.adminUser.error = null;
      state.payment.error = null;
      state.shipping.error = null;
      state.store.error = null;
    },
    clearSettingsData: (state) => {
      state.adminUser.data = null;
      state.payment.data = null;
      state.shipping.data = null;
      state.store.data = null;
    },
  },
  extraReducers: (builder) => {
    // Admin User Settings
    builder
      .addCase(fetchAdminUserSettings.pending, (state) => {
        state.adminUser.loading = true;
        state.adminUser.error = null;
      })
      .addCase(fetchAdminUserSettings.fulfilled, (state, action) => {
        state.adminUser.loading = false;
        state.adminUser.data = action.payload;
      })
      .addCase(fetchAdminUserSettings.rejected, (state, action) => {
        state.adminUser.loading = false;
        state.adminUser.error = action.payload as string;
      })
      .addCase(updateAdminUserSettingsAsync.pending, (state) => {
        state.adminUser.loading = true;
        state.adminUser.error = null;
      })
      .addCase(updateAdminUserSettingsAsync.fulfilled, (state, action) => {
        state.adminUser.loading = false;
        state.adminUser.data = action.payload;
      })
      .addCase(updateAdminUserSettingsAsync.rejected, (state, action) => {
        state.adminUser.loading = false;
        state.adminUser.error = action.payload as string;
      });

    // Payment Settings
    builder
      .addCase(fetchPaymentSettings.pending, (state) => {
        state.payment.loading = true;
        state.payment.error = null;
      })
      .addCase(fetchPaymentSettings.fulfilled, (state, action) => {
        state.payment.loading = false;
        state.payment.data = action.payload;
      })
      .addCase(fetchPaymentSettings.rejected, (state, action) => {
        state.payment.loading = false;
        state.payment.error = action.payload as string;
      })
      .addCase(updatePaymentSettingsAsync.pending, (state) => {
        state.payment.loading = true;
        state.payment.error = null;
      })
      .addCase(updatePaymentSettingsAsync.fulfilled, (state, action) => {
        state.payment.loading = false;
        state.payment.data = action.payload;
      })
      .addCase(updatePaymentSettingsAsync.rejected, (state, action) => {
        state.payment.loading = false;
        state.payment.error = action.payload as string;
      });

    // Shipping Settings
    builder
      .addCase(fetchShippingSettings.pending, (state) => {
        state.shipping.loading = true;
        state.shipping.error = null;
      })
      .addCase(fetchShippingSettings.fulfilled, (state, action) => {
        state.shipping.loading = false;
        state.shipping.data = action.payload;
      })
      .addCase(fetchShippingSettings.rejected, (state, action) => {
        state.shipping.loading = false;
        state.shipping.error = action.payload as string;
      });

    // Store Settings
    builder
      .addCase(fetchStoreSettings.pending, (state) => {
        state.store.loading = true;
        state.store.error = null;
      })
      .addCase(fetchStoreSettings.fulfilled, (state, action) => {
        state.store.loading = false;
        state.store.data = action.payload;
      })
      .addCase(fetchStoreSettings.rejected, (state, action) => {
        state.store.loading = false;
        state.store.error = action.payload as string;
      });
  },
});

export const { clearSettingsError, clearSettingsData } = settingsSlice.actions;
export default settingsSlice.reducer;
